/**
 * Auth Login Controller (EPIC-001 / 1.3)
 *
 * Submits the email/password/rememberMe form to POST /auth/login.
 * On success the backend posts the nm_session cookie and we redirect to
 * /tableau-de-bord.html. On failure we render an error message via the
 * tpl-form-error template (no HTML inline in JS — CLAUDE.md rule 8).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

export default class AuthLoginController extends BaseController {
  static targets = [
    "form",
    "email",
    "password",
    "rememberMe",
    "submit",
    "error",
    "unverifiedBlock",
    "resendButton",
    "resendStatus",
    "inactiveBlock",
    "resignupLink",
  ];

  connect() {
    this.clearError();
    this.hideUnverifiedBlock();
    this.hideInactiveBlock();
    if (this.hasEmailTarget) {
      // Native autofocus on the email field. Done after connect() so the
      // build pipeline can ship the page without auto-focus surprises.
      queueMicrotask(() => this.emailTarget.focus());
    }

    // Picked up after a successful password reset (EPIC-001 / 1.4) — the
    // reset controller redirects here with ?reset=success.
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset") === "success") {
      this.showSuccess(
        "Mot de passe modifié. Connectez-vous avec votre nouveau mot de passe.",
      );
      // Clean the URL so a refresh does not re-toast.
      const cleanedSearch = (() => {
        params.delete("reset");
        const rest = params.toString();
        return rest ? `?${rest}` : "";
      })();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + cleanedSearch + window.location.hash,
      );
    }
  }

  async submit(event) {
    event.preventDefault();

    const email = this.emailTarget.value.trim();
    const password = this.passwordTarget.value;
    const rememberMe = this.hasRememberMeTarget && this.rememberMeTarget.checked;

    this.clearError();
    this.hideUnverifiedBlock();
    this.hideInactiveBlock();

    if (!email || !this.isValidEmail(email)) {
      this.showFormError("Email invalide.");
      this.emailTarget.focus();
      return;
    }

    if (!password) {
      this.showFormError("Mot de passe requis.");
      this.passwordTarget.focus();
      return;
    }

    this.setBusy(true);

    try {
      await api.post("/auth/login", { email, password, rememberMe });
      window.location.assign("/tableau-de-bord.html");
    } catch (err) {
      this.handleLoginError(err);
    } finally {
      this.setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  handleLoginError(err) {
    const status = err instanceof APIError ? err.status : 0;

    // EPIC-002 / D2 — the backend returns 403 EMAIL_NOT_VERIFIED for users
    // who exist + matched their password but haven't validated their email
    // yet. We surface a dedicated block with a "resend" action instead of
    // the generic disabled-account message.
    if (status === 403 && err?.data?.error?.code === "EMAIL_NOT_VERIFIED") {
      this.showUnverifiedBlock();
      return;
    }

    // EPIC-038 / 38.6 §2.K — 403 account_inactive: the demo account was
    // purged after prolonged inactivity. Surface a dedicated block whose CTA
    // re-registers with the same email (same-email signup is allowed — the
    // backend resurrects the account).
    if (status === 403 && err?.data?.error?.code === "account_inactive") {
      this.showInactiveBlock();
      return;
    }

    let message;
    switch (status) {
      case 401:
        message = "Identifiants invalides.";
        break;
      case 403:
        message = "Ce compte est désactivé.";
        break;
      case 422:
        message = "Email ou mot de passe manquant.";
        break;
      case 0:
      case 408:
        message = "Réseau injoignable. Réessayez.";
        break;
      default:
        message = "Une erreur est survenue. Réessayez.";
    }
    this.showFormError(message);
  }

  // ---------------------------------------------------------------------------
  // EMAIL_NOT_VERIFIED block — toggled when the login gate fires (D2).
  // ---------------------------------------------------------------------------

  showUnverifiedBlock() {
    if (!this.hasUnverifiedBlockTarget) return;
    this.unverifiedBlockTarget.hidden = false;
    if (this.hasResendStatusTarget) {
      this.resendStatusTarget.hidden = true;
      this.resendStatusTarget.textContent = "";
    }
    if (this.hasResendButtonTarget) {
      this.resendButtonTarget.disabled = false;
    }
    this.unverifiedBlockTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  hideUnverifiedBlock() {
    if (!this.hasUnverifiedBlockTarget) return;
    this.unverifiedBlockTarget.hidden = true;
    if (this.hasResendStatusTarget) {
      this.resendStatusTarget.hidden = true;
      this.resendStatusTarget.textContent = "";
    }
  }

  // ---------------------------------------------------------------------------
  // account_inactive block — toggled when a purged demo account attempts to
  // log in (EPIC-038 / 38.6). The CTA carries the typed email so the signup
  // form opens pre-filled and the backend resurrection path can match it.
  // ---------------------------------------------------------------------------

  showInactiveBlock() {
    if (!this.hasInactiveBlockTarget) return;
    if (this.hasResignupLinkTarget && this.hasEmailTarget) {
      const email = this.emailTarget.value.trim();
      this.resignupLinkTarget.href = email
        ? `/inscription.html?email=${encodeURIComponent(email)}`
        : "/inscription.html";
    }
    this.inactiveBlockTarget.hidden = false;
    this.inactiveBlockTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  hideInactiveBlock() {
    if (!this.hasInactiveBlockTarget) return;
    this.inactiveBlockTarget.hidden = true;
  }

  async resendVerification() {
    if (!this.hasEmailTarget) return;
    const email = this.emailTarget.value.trim();
    if (!email || !this.isValidEmail(email)) {
      this.setResendStatus("Saisissez votre email avant de demander un nouveau lien.");
      return;
    }

    if (this.hasResendButtonTarget) {
      this.resendButtonTarget.disabled = true;
      this.resendButtonTarget.setAttribute("aria-busy", "true");
    }
    this.setResendStatus("Envoi en cours…");

    try {
      await api.post("/auth/resend-verification", { email });
      this.setResendStatus("Si un compte existe et n'est pas vérifié, un nouveau lien vient d'être envoyé.");
    } catch (err) {
      const status = err instanceof APIError ? err.status : 0;
      if (status === 0 || status === 408 || status >= 500) {
        this.setResendStatus("Réseau injoignable. Réessayez dans un instant.");
      } else {
        this.setResendStatus("Une erreur est survenue. Réessayez.");
      }
    } finally {
      // Keep the button disabled briefly to discourage spam, then re-enable.
      setTimeout(() => {
        if (this.hasResendButtonTarget) {
          this.resendButtonTarget.disabled = false;
          this.resendButtonTarget.setAttribute("aria-busy", "false");
        }
      }, 30_000);
    }
  }

  setResendStatus(message) {
    if (!this.hasResendStatusTarget) return;
    this.resendStatusTarget.textContent = message;
    this.resendStatusTarget.hidden = false;
  }

  showFormError(message) {
    if (!this.hasErrorTarget) return;

    // Use the shared <template id="tpl-form-error"> defined in
    // partials/templates.html so we never inject raw HTML strings here.
    const fragment = this.cloneTemplate("tpl-form-error");
    this.fillSlots(fragment, { message });

    this.errorTarget.replaceChildren(fragment);
    this.errorTarget.hidden = false;
  }

  clearError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.replaceChildren();
    this.errorTarget.hidden = true;
  }

  // ---------------------------------------------------------------------------
  // Submit button busy state — disables to avoid double submit and
  // gives a small visual cue.
  // ---------------------------------------------------------------------------

  setBusy(busy) {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.disabled = busy;
    this.submitTarget.setAttribute("aria-busy", busy ? "true" : "false");
  }
}
